import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal, viewChild } from '@angular/core';
import { TreeListAction, TreeListColumn } from '@app/orders/models/dx-tree-list.model';
import { TreeListRowData } from '@app/orders/models/orders.model';
import { OrdersActionsService } from '@app/orders/services/orders-actions.service';
import { OrdersCellRenderingService } from '@app/orders/services/orders-cell-rendering.service';
import { OrdersSelectionService } from '@app/orders/services/orders-selection.service';
import { OrdersTreeDatasourceService } from '@app/orders/services/orders-tree-datasource.service';
import { OrdersTreeListRefreshService } from '@app/orders/services/orders-tree-list-refresh.service';
import { OrdersTreeListFacade } from '@app/orders/services/orders-tree-list.facade';
import { OrdersTreeListStore } from '@app/orders/services/orders-tree-list.store';
import { TreeListUtilsService } from '@app/orders/services/tree-list-utils.service';
import { DestroyableComponent } from '@core/components/destroyable/destroyable.component';
import { ChipItem, OffcanvasComponent } from '@shared/components';
import { TableUtilsService } from '@shared/utils/table-utils.service';
import { DxTreeListComponent } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import dxTreeList, { CellPreparedEvent, RowDblClickEvent, SelectionChangedEvent } from 'devextreme/ui/tree_list';
import { takeUntil } from 'rxjs/operators';
import { OrdersDetailsComponent } from '../orders-details/orders-details.component';

@Component({
  selector: 'app-orders-tree-list',
  standalone: false,
  templateUrl: './orders-tree-list.component.html',
  styleUrls: ['./orders-tree-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TreeListUtilsService,
    TableUtilsService,
    OrdersTreeDatasourceService,
    OrdersSelectionService,
    OrdersActionsService,
    OrdersCellRenderingService,
    OrdersTreeListStore,
    OrdersTreeListFacade,
  ],
})
export class OrdersTreeListComponent
  extends DestroyableComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  treeList = viewChild<DxTreeListComponent>('tree');
  offcanvas = viewChild<OffcanvasComponent>(OffcanvasComponent);
  readonly detailsComponent = OrdersDetailsComponent;

  readonly facade = inject(OrdersTreeListFacade);
  private readonly refreshService = inject(OrdersTreeListRefreshService);

  readonly currentDataLength = this.facade.currentDataLength;
  readonly actualPageSize = this.facade.actualPageSize;
  readonly totalPages = this.facade.totalPages;

  readonly selectedRows = this.facade.selectedRows;
  readonly selectedTotalAmount = this.facade.selectedTotalAmount;
  readonly selectedSeatsCount = this.facade.selectedSeatsCount;
  readonly selectedFoldersCount = this.facade.selectedFoldersCount;
  readonly selectedOrdersCount = this.facade.selectedOrdersCount;
  readonly isSelectAllValidationPending = this.facade.isSelectAllValidationPending;
  readonly selectedFoldersIds = this.facade.selectedFoldersIds;

  readonly currentPage = this.facade.currentPage;
  readonly pageSize = this.facade.pageSize;
  readonly categoryTooltip = this.facade.categoryTooltip;
  readonly rateTooltip = this.facade.rateTooltip;

  // Signals : assignés dans ngAfterViewInit (après la 1re CD) ; un signal re-marque
  // automatiquement la vue OnPush, sans dépendre des events DevExtreme.
  readonly actions = signal<TreeListAction[]>([]);
  readonly fixedActions = signal<TreeListAction[]>([]);
  treeOptions = this.facade.treeOptions;
  dataSource: CustomStore<TreeListRowData, number>;
  columns: TreeListColumn[];
  onContentReadyCallback: (event: any) => void;

  orderItemRowType = this.facade.orderItemRowType;
  customerTypeEnumResponseDto = this.facade.customerTypeEnumResponseDto;
  identificationType = this.facade.identificationType;

  getTreeInstance(): dxTreeList | undefined {
    return this.treeList()?.instance;
  }

  get isItemsCheckboxSelected(): boolean {
    return this.facade.isItemsCheckboxSelected;
  }

  get totalCount(): number {
    return this.facade.totalCount;
  }

  ngOnInit(): void {
    this.facade.setTreeInstanceGetter(() => this.getTreeInstance());
    this.facade.init();
    this.dataSource = this.facade.dataSource;
    this.columns = this.facade.columns;
    this.onContentReadyCallback = this.facade.onContentReadyCallback;
    this.listenToRefreshRequests();
  }

  private listenToRefreshRequests(): void {
    this.refreshService.refresh$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.facade.refreshTreeList({ treeInstance: this.getTreeInstance() });
      },
    });
  }

  ngAfterViewInit(): void {
    const offcanvas = this.offcanvas();
    if (!offcanvas) return;
    this.facade.initActions(offcanvas);
    this.actions.set(this.facade.actions);
    this.fixedActions.set(this.facade.fixedActions);
  }

  listenToPrintRequestCompleted(): void {
    this.facade.listenToPrintRequestCompleted();
  }

  onPageChanged(page: number): void {
    this.facade.onPageChanged(page);
  }

  onPageSizeChanged(size: number): void {
    this.facade.onPageSizeChanged(size);
  }

  deselectAllRows(): void {
    this.facade.deselectAllRows();
  }

  deselectAllRowsFromActionBar(): void {
    this.facade.deselectAllRowsFromActionBar();
  }

  onSelectionChanged(event: SelectionChangedEvent): void {
    this.facade.onSelectionChanged(event);
  }

  toggleRow(key: number): void {
    this.facade.toggleRow(key);
  }

  toggleOrderFolders(orderId: number): void {
    this.facade.toggleOrderFolders(orderId);
  }

  getStatusChipData(rowData: TreeListRowData): ChipItem[] {
    return this.facade.getStatusChipData(rowData);
  }

  isExpanded(key: number): boolean {
    return this.facade.isExpanded(key);
  }

  isOrderSelected(orderId: number): boolean {
    return this.facade.isOrderSelected(orderId);
  }

  isColumnSorted(column: any, sortOrder: 'asc' | 'desc'): boolean {
    return this.facade.isColumnSorted(this.getTreeInstance(), column, sortOrder);
  }

  sortColumn(event: Event, column: any, sortOrder: 'asc' | 'desc', emit: boolean): void {
    this.facade.sortColumn(event, this.getTreeInstance(), column, sortOrder, emit);
  }

  onCellPrepared(event: CellPreparedEvent): void {
    this.facade.onCellPrepared(event);
  }

  onRowDblClick(rowData: RowDblClickEvent): void {
    const offcanvas = this.offcanvas();
    if (!offcanvas) return;
    this.facade.onRowDblClick(rowData, offcanvas);
  }

  closeOrderDetails(): void {
    this.facade.closeOrderDetails();
  }

  loadMultiCategories(rowData: TreeListRowData): void {
    this.facade.loadMultiCategories(rowData);
  }

  loadMultiRates(rowData: TreeListRowData): void {
    this.facade.loadMultiRates(rowData);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.facade.destroy();
  }
}
